/**
 * Promise utilities for handling timeouts and preventing infinite loading states
 */

/**
 * Custom error class for timeout errors
 */
export class TimeoutError extends Error {
  isTimeout = true;

  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise with a timeout. Returns the promise result or throws TimeoutError.
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param operationName - Name of the operation for logging/errors
 * @returns Resolves with promise result or rejects with TimeoutError
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  operationName = 'Operation'
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new TimeoutError(`${operationName} timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Wraps a promise with timeout but returns fallback value instead of throwing
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param fallbackValue - Value to return on timeout
 * @param operationName - Name for logging
 * @returns Resolves with result or fallback
 */
export async function withTimeoutFallback<T>(
  promise: Promise<T>,
  timeoutMs: number,
  fallbackValue: T,
  operationName = 'Operation'
): Promise<T> {
  try {
    return await withTimeout(promise, timeoutMs, operationName);
  } catch (error) {
    if (error instanceof TimeoutError) {
      console.warn(`[${operationName}] Timed out after ${timeoutMs}ms, using fallback`);
      return fallbackValue;
    }
    throw error;
  }
}
